'use client'

import { Button, type ButtonProps } from '@repo/ui'
import { ArrowDiagonalUp } from '@repo/ui/icons'
import NextLink from 'next/link'
import {
  cloneElement,
  type ComponentProps,
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  isValidElement,
  type ReactNode
} from 'react'

import { Avatar, type AvatarProps } from './Avatar'

type LinkMode = 'internal' | 'external'

type IconProps = { className?: string; style?: CSSProperties }

/** Pick the square icon-button size matching a text size's height. */
const toIconSize = (size: ButtonProps['size']): ButtonProps['size'] => {
  switch (size) {
    case 'sm':
    case 'iconSm':
      return 'iconSm'
    case 'md':
    case 'iconMd':
      return 'iconMd'
    case 'lg':
    case 'iconLg':
      return 'iconLg'
    default:
      return 'icon'
  }
}

/** Ergonomic content contract forwarded to the embedded `@repo/ui` Button. */
export interface ButtonLinkButtonProps {
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  label?: ReactNode
  labelClassName?: string
  iconLeft?: ReactNode
  iconLeftProps?: IconProps
  iconRight?: ReactNode
  iconRightProps?: IconProps
  iconAlone?: ReactNode
  iconAloneProps?: IconProps
  avatarProps?: AvatarProps
  className?: string
  disabled?: boolean
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  ['data-testid']?: string
}

export interface ButtonLinkProps {
  /** The URL the link navigates to */
  href: ComponentProps<typeof NextLink>['href']
  /** Whether the link is internal or external. Defaults to "external" */
  mode?: LinkMode
  /** Props forwarded to the inner Button */
  buttonProps?: ButtonLinkButtonProps
  /** Controls Next.js Link prefetching behavior */
  prefetch?: boolean
  /** Additional CSS classes for the link wrapper */
  className?: string
}

const withProps = (element: ReactNode, props: IconProps | undefined): ReactNode =>
  isValidElement<IconProps>(element) ? cloneElement(element, props ?? {}) : (element ?? null)

const ButtonLink = (
  { mode = 'external', href, buttonProps, prefetch, className }: ButtonLinkProps,
  ref: ForwardedRef<HTMLAnchorElement>
) => {
  const isExternal = mode === 'external'

  const {
    variant,
    size,
    label,
    labelClassName,
    iconLeft,
    iconLeftProps,
    iconRight,
    iconRightProps,
    iconAlone,
    iconAloneProps,
    avatarProps,
    className: buttonClassName,
    ...restButtonProps
  } = buttonProps ?? {}

  const isIconOnly = iconAlone != null && label == null && avatarProps == null

  const rightIcon = isExternal
    ? withProps(<ArrowDiagonalUp />, iconRightProps)
    : withProps(iconRight, iconRightProps)

  const content = isIconOnly ? (
    withProps(iconAlone, iconAloneProps)
  ) : (
    <>
      {avatarProps && <Avatar {...avatarProps} />}
      {withProps(iconLeft, iconLeftProps)}
      {label != null && (labelClassName ? <span className={labelClassName}>{label}</span> : label)}
      {rightIcon}
    </>
  )

  return (
    <NextLink
      href={href}
      ref={ref}
      prefetch={prefetch ?? (isExternal ? false : undefined)}
      className={className}
      {...(isExternal && {
        target: '_blank',
        rel: 'noreferrer noopener'
      })}
    >
      <Button
        asChild
        variant={variant}
        size={isIconOnly ? toIconSize(size) : size}
        className={buttonClassName}
        {...restButtonProps}
      >
        <span>{content}</span>
      </Button>
    </NextLink>
  )
}

const ForwardedButtonLink = forwardRef(ButtonLink)
ForwardedButtonLink.displayName = 'ButtonLink'

export { ForwardedButtonLink as ButtonLink }
export type { LinkMode }
